import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router';
import LargeCard from '../../main/large-card';
import {
    LoadObjectStats,
    MansionIdToStats,
} from '../../../store/objectStatsSlice';
import { isFinal, RequestStatus } from '../../../store/network/managedRequest';
import { RootState } from '../../../store';
import { ManagedPartnerDto } from '../../../store/partnerManagerSlice';
import useTranslate from '../../useTranslate';
import { PbRequest } from '../../../store/pbRequest';

interface Props {
    getObjectStatsStatus: RequestStatus;
    mansionIdToStats: MansionIdToStats;
    LoadObjectStats: () => void;
    selectedPartner: ManagedPartnerDto;
}

interface Stats {
    turnover: number;
    users: number;
    wash_cycles: number;
}

function DashboardStatistics(props: Readonly<Props>) {
    const strings = useTranslate('DashboardStatistics');

    const [objectStatsSum, setObjectStatsSum] = useState<Stats>(null);

    useEffect(() => {
        props.LoadObjectStats();
    }, []);

    useEffect(() => {
        if (isFinal(props.getObjectStatsStatus)) {
            const stats = calculateStats(props.mansionIdToStats);
            setObjectStatsSum(stats);
        }
    }, [props.getObjectStatsStatus, props.mansionIdToStats]);

    if (!objectStatsSum) {
        return (
            <LargeCard
                type="card-info"
                title={[strings.statistics1, strings.statistics2]}
            />
        );
    } else {
        return (
            <LargeCard
                type="card-edit"
                title={[strings.statistics1, strings.statistics2]}
            >
                <Row className="mt-10">
                    <Col xs={4}>
                        <div
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title={strings.wonThisMonth}
                        >
                            <span className="icon icon-wew-calendar-euro-large icon-lg">
                                <i className="symbol">
                                    {strings._utils.getCurrencySymbolFromAccountingArea(
                                        props.selectedPartner
                                    )}
                                </i>
                            </span>
                            <div className="icon-label icon-label-lg">
                                {strings._utils.formatNumber(
                                    objectStatsSum.turnover || 0,
                                    2
                                )}
                            </div>
                            <div className="icon-label">
                                {' '}
                                {strings.wonThisMonth}
                            </div>
                        </div>
                    </Col>
                    <Col xs={4} className=" divider-lr">
                        <div
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title={strings.numberOfUsers}
                        >
                            <span className="icon icon-wew-users-large icon-lg" />
                            <div className="icon-label icon-label-lg">
                                {objectStatsSum.users || '0'}
                            </div>
                        </div>
                        <div className="icon-label">
                            {' '}
                            {strings.numberOfUsers}
                        </div>
                    </Col>
                    <Col xs={4}>
                        <div
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title={strings.numberOfCycles}
                        >
                            <span className="icon icon-wew-washmachine-large icon-lg" />
                            <div className="icon-label icon-label-lg">
                                {objectStatsSum.wash_cycles || '0'}
                            </div>
                        </div>
                        <div className="icon-label">
                            {' '}
                            {strings.numberOfCycles}
                        </div>
                    </Col>
                </Row>
                <Row className={'mt-20'}>
                    <Col xs={8} />
                    <Col xs={4}>
                        <Link to={`/usage-data/${getCurrentContractPeriod()}`}>
                            {strings.goToDetails}
                        </Link>
                    </Col>
                </Row>
            </LargeCard>
        );
    }
}

function getCurrentContractPeriod() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return year * 100 + month;
}

function calculateStats(mansionIdToStats: MansionIdToStats): Stats {
    const zeroStats: Stats = {
        turnover: 0.0,
        users: 0,
        wash_cycles: 0,
    };

    return Object.values(mansionIdToStats).reduce(
        (result, value) => ({
            turnover: result.turnover + value.turnover,
            users: result.users + value.users,
            wash_cycles: result.wash_cycles + value.wash_cycles,
        }),
        zeroStats
    );
}

export default connect(
    (store: RootState) => ({
        getObjectStatsStatus:
            store.pbRequest[PbRequest.GET_OBJECT_STATS].status,
        mansionIdToStats: store.objectStats.mansionIdToStats,
        selectedPartner: store.partnerManager.selectedPartner,
    }),
    { LoadObjectStats }
)(DashboardStatistics);
